/* eslint-disable @typescript-eslint/no-explicit-any -- debug tools use dynamic this-binding to app instance */
import * as R from 'ramda';

import { $, $$, ad, del } from '@utils/dom.ts';
import { singleFile } from '@utils/funcs.ts';

import { orphanTest } from '../plugins/wayfinder/src/navGraphDebug.ts';

if (typeof window !== 'undefined') {
  window.R = R;
} // helps use Rambda in console... probably don't want to do this once we integrate with customer content

// NOTE:
// The exported functions from this module all get placed into the app.debug (accessable from console via _app.debug) and bound to
// the app instance. So the `this` context will contain the app instance when these are run. This allows you to access objects like
// the bus via `this.bus` or configuration via `this.config`
// Update: these are now also available in MoL by searching for `debug:<funcname>` - such as `debug:showIcons`

const showIcons = () => {
  $('#mapRenderDiv').innerHTML =
    '<style> div { display: inline-block; text-align: center; border: 1px solid lightblue; }</style>' +
    $$('svg symbol')
      .map(e => `<div><svg><use xlink:href="#${e.id}"/></svg><br/>${e.id}</div>`)
      .join('');
};

// TODO: Need to clean this up
function poisByCategory(this: any) {
  return this.bus
    .send('poi/getAll')
    .then((r: any) => r[0])
    .then((p: any) => Object.values(p))
    .then(R.groupBy((o: any) => o.category));
}

const highlightNodes = () =>
  ad(
    {
      tag: 'style',
      html: '* { background-color: rgba(255,0,0,.2); } * * { background-color: rgba(0,255,0,.2); } * * * { background-color: rgba(0,0,255,.2); } * * * * { background-color: rgba(255,0,255,.2); } * * * * * { background-color: rgba(0,255,255,.2); } * * * * * * { background-color: rgba(255,255,0,.2); } * * * * * * * { background-color: rgba(255,0,0,.2); } * * * * * * * * { background-color: rgba(0,255,0,.2); } * * * * * * * * * { background-color: rgba(0,0,255,.2); }',
    },
    $('head'),
  );

function getPoiById(this: any, id: string) {
  return this.bus.send('poi/getById', { id }).then((r: any) => r[0]);
}

async function showOrphaned(this: any) {
  const navGraph = await this.bus.get('wayfinder/_getNavGraph');
  const oob = orphanTest(navGraph._nodes);
  this.bus.send('map/showOrphanedGraphNodes', { orphanedNodes: oob.orphaned });
}

async function showgraph(this: any) {
  const navGraph = await this.bus.get('wayfinder/getNavGraphFeatures');
  this.bus.send('map/showNavGraphFeatures', { navGraph });
  monitorDebugFeaturesReset('map/resetNavGraphFeatures', this.bus);
}

async function bounds(this: any) {
  const venueCenter = await this.bus.get('venueData/getVenueCenter');
  const { venueRadius, bounds } = await this.bus.get('venueData/getVenueData');
  this.bus.send('map/showVenueBounds', { venueCenter, venueRadius, bounds });
  monitorDebugFeaturesReset('map/resetVenueBounds', this.bus);
}

async function buildingBounds(this: any, nameFilter: any) {
  this.bus.send('map/showBuildingBounds', { nameFilter });
  monitorDebugFeaturesReset('map/resetVenueBounds', this.bus);
  showCenter.apply(this);
}

async function floorBounds(this: any, nameFilter: any) {
  const bus = this.bus;
  bus.send('map/showFloorBounds', { nameFilter });

  monitorDebugFeaturesReset('map/resetVenueBounds', bus);

  const unsubFloorChanges = bus.monitor('map/floorChanged', () => bus.send('map/showFloorBounds', { nameFilter }));
  onNextSearchClear(unsubFloorChanges, bus);
  showCenter.apply(this);
}

async function padding(this: any) {
  this.bus.send('map/togglePadding');
  monitorDebugFeaturesReset('map/togglePadding', this.bus);
}

const monitorDebugFeaturesReset = (resetEventName: string, bus: any) =>
  onNextSearchClear(() => bus.send(resetEventName), bus);

const onNextSearchClear = (fn: () => void, bus: any) => {
  const unsubscribe = bus.monitor('homeview/performSearch', ({ term }: { term: string }) => {
    if (!term) {
      fn();
      unsubscribe();
    }
  });
};

async function showCenter(this: any) {
  const padding = await this.bus.get('map/getPadding');
  const leftAdj = (padding.left - padding.right) / 2; // how much to adjust left from true center to account for padding
  const topAdj = (padding.top - padding.bottom) / 2; // how much to adjust top from true center to account for padding
  const crosshair = ad(
    {
      html: `<svg width="100" height="100" xmlns="http://www.w3.org/2000/svg"><path stroke="#fF000080" stroke-width="2" d="M0 50h45M55 50h45M50 0v45M50 55v45"/></svg>`,
      styles: {
        position: 'absolute',
        'inset-inline-start': '50%',
        top: '50%',
        'margin-inline-start': -50 + leftAdj + 'px',
        'margin-top': -50 + topAdj + 'px',
      },
    },
    $('.maplibregl-map'),
  );
  onNextSearchClear(() => del(crosshair), this.bus);
}

function dndGo(this: any) {
  const bus = this.bus;
  const toggleActive = (e: DragEvent, indicateDragVisual: boolean) => {
    e.preventDefault();
    if (indicateDragVisual) {
      (e.target as HTMLElement).classList.add('dragover');
    } else {
      (e.target as HTMLElement).classList.remove('dragover');
    }
  };

  const handlers: Record<string, (e: DragEvent) => void> = {
    drop: async function (e: DragEvent) {
      toggleActive(e, false);

      // Load the file into the player
      for (const file of e.dataTransfer!.files) {
        const filename = file.name;
        if (file.type === 'application/json' || file.type.startsWith('text/')) {
          const reader = new FileReader();
          reader.onload = singleFile(async (e: any) =>
            bus.send(`debugTools/fileDrop`, {
              file,
              filename,
              content: e.target.result,
            }),
          );
          reader.readAsText(file);
        }
      }
    },

    // Drag-over event
    dragover: function (e: DragEvent) {
      toggleActive(e, true);
    },

    // Drag-leave event
    dragleave: function (e: DragEvent) {
      toggleActive(e, false);
    },
  }; // end of handlers def

  Object.keys(handlers).forEach(function (event) {
    document.body.addEventListener(event, handlers[event] as EventListener);
  });

  console.log('DnD Listeners installed');
} // end of function installDragNDrop
/* eslint-enable @typescript-eslint/no-explicit-any */

export default {
  bounds,
  buildingBounds,
  dndGo,
  floorBounds,
  getPoiById,
  highlightNodes,
  orphanTest,
  padding,
  poisByCategory,
  showCenter,
  showgraph,
  showIcons,
  showOrphaned,
};
